import React, { useState } from "react";
import {
  Card,
  Space,
  message,
  Row,
  Col,
  Descriptions,
  Typography,
  Dropdown,
  Alert,
} from "antd";
import { AimOutlined, LinkOutlined } from "@ant-design/icons";
import OrgLinks from "./OrgLinks";
import { useIsMobile } from "../../../hooks/useIsMobile";
import FieldSubscriptionStartedAt from "./FieldSubscriptionStartedAt";
import FieldIstanza from "./FieldIstanza";
import FieldTipoLicenza from "./FieldTipoLicenza";
import FieldLimiteServizi from "./FieldLimiteServizi";
import FieldDataScadenza from "./FieldDataScadenza";
import FieldRecurringBillingFrequency from "./FieldRecurringBillingFrequency";
import OrgLimits from "./OrgLimits";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import OrgMap from "./OrgMap";
import ServiziUsage from "../../shared/ServiziUsage";
import OrgDefuntiAnnual from "./OrgDefuntiAnnual";
import OrgChurnPanel from "./OrgChurnPanel";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";

const { Link: AntLink } = Typography;

const OrgInfo = ({ org }) => {
  const [activeBilling, setActiveBilling] = useState(null);
  const { isProd, assumeOrg, assumeUnifiedOrg } = useAppContext();
  const orgId = org.objectId;
  const isMobile = useIsMobile();

  const assumeOrgRole = async (orgId) => {
    try {
      message.loading("Ogni suo desiderio è un ordine", 2);
      await assumeOrg(orgId);

      message.success("Super User attivato", 2);
    } catch (error) {
      message.error("Si é verificato un problema: " + error.message);
      console.error(error);
    }
  };

  const formatDateWithRelative = (date) => {
    const formattedDate = new Date(date).toLocaleDateString();
    const relativeTime = formatDistanceToNow(new Date(date), {
      addSuffix: true,
      locale: it,
    });
    return `${formattedDate} (${relativeTime})`;
  };

  const enterAsEterno = (orgId) => {
    assumeOrgRole(orgId).then(() => {
      // Use environment-aware URL for Eterno logout
      const eternoUrl = `${config.ofcUrl[isProd ? "prod" : "dev"]}/logout.php`;
      window.open(eternoUrl, "_blank");
    });
  };

  const enterAsGiotto = (orgId) => {
    assumeOrgRole(orgId).then(() => {
      const giottoUrl = `${config.giottoUrl[isProd ? "prod" : "dev"]}/logout`;
      window.open(giottoUrl, "_blank");
    });
  };

  const enterAsFuneralCloud = async (orgId) => {
    try {
      message.loading("Ogni suo desiderio è un ordine", 2);
      const result = await assumeUnifiedOrg({ orgId });
      if (result?.funeralcloud?.status === "orphan") {
        message.warning("Questa organizzazione non ha un account FuneralCloud collegato", 4);
        return;
      }
      message.success("Super User attivato su FuneralCloud", 2);
      const fcUrl = `${config.funeralcloudUrl[isProd ? "prod" : "dev"]}/logout`;
      window.open(fcUrl, "_blank");
    } catch (error) {
      message.error("Si è verificato un problema: " + error.message);
    }
  };

  const onMenuClick = (e, orgId) => {
    if (e.key === "eterno") {
      enterAsEterno(orgId);
    } else if (e.key === "giotto") {
      enterAsGiotto(orgId);
    } else if (e.key === "funeralcloud") {
      enterAsFuneralCloud(orgId);
    }
  };

  const items = [
    {
      key: "eterno",
      label: "Eterno",
    },
    {
      key: "giotto",
      label: "Giotto",
    },
    {
      key: "funeralcloud",
      label: "FuneralCloud",
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <OrgChurnPanel org={org} />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size="large">
            <Card title="Azioni e Link rapidi" className="org-info-card">
              <Space direction={isMobile ? "vertical" : "horizontal"} wrap style={{ width: "100%" }}>
                <Dropdown.Button
                  menu={{
                    items,
                    onClick: (e) => onMenuClick(e, orgId),
                  }}
                  onClick={() => enterAsEterno(orgId)}
                  size={isMobile ? "middle" : "large"}
                  icon={<AimOutlined />}
                  style={isMobile ? { width: "100%" } : undefined}
                >
                  Entra come Super User
                </Dropdown.Button>
                <OrgLinks org={org} size={isMobile ? "small" : "large"} />
              </Space>
            </Card>
            <Card title="Anagrafica" bordered={false}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Descriptions column={1} size="small" layout={isMobile ? "vertical" : "horizontal"}>
                    <Descriptions.Item label="Org ID">
                      {org.objectId}
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      {org.email}
                    </Descriptions.Item>
                    <Descriptions.Item label="Telefono">
                      {org.telefono1}{" "}
                      {org.telefono2 ? `/ ${org.telefono2}` : ""}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cellulare">
                      {org.cellulare1}{" "}
                      {org.cellulare2 ? `/ ${org.cellulare2}` : ""}
                    </Descriptions.Item>
                    <Descriptions.Item label="Creato il">
                      {formatDateWithRelative(org.createdAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ultimo Aggiornamento">
                      {formatDateWithRelative(org.updatedAt)}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={24} md={12}>
                  <Descriptions column={1} size="small" layout={isMobile ? "vertical" : "horizontal"}>
                    <Descriptions.Item label="Indirizzo">
                      {org.indirizzo}, {org.cap} {org.citta} ({org.provincia}),{" "}
                      {org.nazione}
                    </Descriptions.Item>
                    <Descriptions.Item label="Partita IVA">
                      {org.partita_iva}
                    </Descriptions.Item>
                    <Descriptions.Item label="Sito Web">
                      {org.sito ? (
                        <AntLink
                          href={
                            org.sito.startsWith("http")
                              ? org.sito
                              : `https://${org.sito}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {org.sito} <LinkOutlined />
                        </AntLink>
                      ) : (
                        "N/A"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="PEC">
                      {org.pec || "N/A"}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>
            <Card title="Abbonamento" bordered={false}>
              {org?.tipo_licenza && org.tipo_licenza !== "module" && (
                <Alert
                  type="warning"
                  message="Questa organizzazione dovrebbe essere migrata ai moduli"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}
              <Descriptions column={1} size="small" layout={isMobile ? "vertical" : "horizontal"}>
                <Descriptions.Item label="Istanza">
                  <FieldIstanza org={org} />
                </Descriptions.Item>
                <Descriptions.Item label="Tipo Licenza">
                  <FieldTipoLicenza org={org} />
                </Descriptions.Item>
                <Descriptions.Item label="Data Inizio Abbonamento">
                  <FieldSubscriptionStartedAt org={org} />
                </Descriptions.Item>
                <Descriptions.Item label="Frequenza Fatturazione">
                  <FieldRecurringBillingFrequency org={org} />
                </Descriptions.Item>
                <Descriptions.Item label="Data Scadenza">
                  <FieldDataScadenza org={org} fieldName="data_scadenza" />
                </Descriptions.Item>
                <Descriptions.Item label="Limite Servizi">
                  <FieldLimiteServizi org={org} />
                </Descriptions.Item>
              </Descriptions>
              {!(String(org?.tipo_istanza || '').toLowerCase() === 'demo' || String(org?.tipo_istanza || '').toLowerCase() === 'free') && (
                <div style={{ marginTop: 16 }}>
                  <Typography.Title level={5} style={{ marginTop: 0 }}>
                    Utilizzo Servizi
                  </Typography.Title>
                  {activeBilling && (
                    <div style={{ marginTop: 8, marginBottom: 12 }}>
                      <Typography.Title level={5} style={{ marginTop: 0 }}>
                        Periodo attivo
                      </Typography.Title>
                      <div style={{ maxWidth: 720 }}>
                        <ServiziUsage org={org} mode="expanded" stats={activeBilling} />
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      Periodi precedenti
                    </Typography.Title>
                    <OrgDefuntiAnnual orgId={org.objectId} onActive={(row) => setActiveBilling(row)} />
                  </div>
                </div>
              )}
            </Card>
            <OrgLimits org={org} />
          </Space>
        </Col>
        <Col xs={24} lg={8}>
          <OrgMap orgId={org.objectId} />
        </Col>
      </Row>
    </Space>
  );
};

export default OrgInfo;
